<template>
  <v-container grid-list-lg>
    <v-layout v-if="value !== null">
      <v-flex>
        <v-row v-if="!readonly">
          <v-col class="mb-2" cols="12" md="6">
            <address-autocomplete
                :type="type"
                :addresses="addresses"
                @input="onAutocomplete"/>
          </v-col>
        </v-row>
        <v-layout wrap>
          <fp-input hide-details
                    :label="I18NGetter().useAddressForm.STREET"
                    xs12 :md6="!dense"
                    :disabled="readonly"
                    :required="isRequiredEnabled('street')"
                    v-model="value.street"
                    :id="$cy.AddressFormStreet"/>
<!--          jeżeli nie ma required w propsach to nie ma sprawdzać customRules, jeżeli jest required
              i usuwany jest numer domu (initialStreetNumber istnieje) to mają się załączyć customRulesy o usuwaniu numeru
              -->
          <fp-input
                    xs12 :md3="!dense"
                    :label="I18NGetter().useAddressForm.STREET_NUM"
                    :disabled="readonly"
                    :customRules="[ required ? checkIfInitialStreetNumberExists : true,]"
                    v-model="value.streetNum"
                    :clearable="false"
                    :required="!!initialStreetNumber ? false : required"
                    :id="$cy.AddressFormStreetNum"
          />
          <fp-input v-if="!readonly || (readonly && value.streetLocal)"
                    hide-details
                    xs12 :md3="!dense"
                    :label="I18NGetter().useAddressForm.STREET_LOCAL"
                    :disabled="readonly"
                    v-model="value.streetLocal"
                    @input="setWhitespaceIfNull"
                    :id="$cy.AddressFormStreetLocal"
          />
        </v-layout>
        <v-layout wrap>
          <fp-input :label="I18NGetter().useAddressForm.CITY"
                    xs12 :md6="!dense"
                    :disabled="readonly"
                    :required="isRequiredEnabled('city')"
                    v-model="value.city"
                    :id="$cy.AddressFormCity"/>
          <fp-input
            v-model="value.zip"
            :id="$cy.AddressFormZip"
            :preserve-mask="true"
            :label="I18NGetter().useAddressForm.ZIP_CODE"
            :mask="$env.getAppDomainConfig().zipCodeMask"
            :disabled="readonly"
            :required="isRequiredEnabled('zip')"
            xs12 :md4="!dense"
          />
          <fp-input :label="I18NGetter().useAddressForm.POST"
                    v-if="longForm"
                    xs12 :md6="!dense"
                    :disabled="readonly"
                    :required="isRequiredEnabled('post')"
                    v-model="value.post"
                    :id="$cy.AddressFormPost"/>
        </v-layout>
        <v-layout wrap>
          <fp-input :label="I18NGetter().useAddressForm.CITY_DISTRICT"
                    v-if="longForm"
                    xs12 :md6="!dense"
                    :disabled="readonly"
                    v-model="value.cityDistrict"
                    :id="$cy.AddressFormCityDistrict"/>
        </v-layout>
        <v-layout wrap>
          <fp-select v-if="withCountry" :label="I18NGetter().useAddressForm.COUNTRY"
                     xs12 :md6="!dense"
                     :disabled="readonly || disabledFields.includes('country')"
                     :required="isRequiredEnabled('country')"
                     v-model="value.country"
                     :items="multiformDictionary.countriesDictionary"
                     :id="$cy.AddressFormCountry"/>
        </v-layout>
        <v-layout wrap>
          <fp-select :label="I18NGetter().useAddressForm.LAND_ID"
                     xs12 :md6="!dense"
                     :disabled="readonly"
                     :required="isRequiredEnabled('landId')"
                     v-model="value.landId"
                     :items="geoAreas.landDictionary()"
                     :id="$cy.AddressFormLand" />
        </v-layout>
        <v-layout>
          <fp-select :label="I18NGetter().useAddressForm.DISTRICT_ID"
                     xs12 :md6="!dense"
                     v-if="$env.appDomainFeatures.isDistrictsOn"
                     :disabled="readonly || !value.landId"
                     :items="geoAreas.districtDictionary(value.landId)"
                     :required="isRequiredEnabled('districtId')"
                     :id="$cy.AddressFormDistrict"
                     v-model="value.districtId"/>
          <fp-input
            v-if="(longForm && withCommunity) && (!readonly || value.community) && $env.isAppDomain().LENDI_PL"
            v-model="value.community"
            xs12 :md6="!dense"
            :label="I18NGetter().useAddressForm.COMMUNITY"
            :disabled="readonly || !value.districtId"
            :required="isRequiredEnabled('community')"
            :id="$cy.AddressFormCommunity"
          />
        </v-layout>
      </v-flex>
    </v-layout>
  </v-container>
  <!--TODO: Dodać skeleton loader-->
</template>

<script lang="ts">
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {Address, AddressDTO} from '@/modules/multiForm/shared/MultiformModels';
import MultiformDictionary, {getCommunityByDistrictId} from '@/modules/multiForm/shared/MultiformDictionaries';
import AddressAutocomplete from '@/modules/googleAutocomplete/AddressAutocomplete.vue';
import {AutocompletePlacesTypes} from '@/modules/googleAutocomplete/enums/AutocompletePlacesTypes';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {I18NInterface} from '@/services/enumTranslator/I18NInterface';
import useGeoAreas from '@/composables/useGeoAreas';

type AddressKeys = keyof AddressDTO;
type RequiredFields = Partial<Record<AddressKeys, boolean>>;

@Component({
  components: {AddressAutocomplete, },
})
export default class AddressForm extends Vue {
  @Prop({type: Object, })
  public readonly value!: Nullable<Address>;

  @Prop({default: true, },)
  public readonly isClient!: boolean;

  @Prop({default: false, type: Boolean, },)
  public readonly dense!: boolean;

  @Prop({default: false, type: Boolean, },)
  public readonly required!: boolean;

  @Prop({default: true, })
  public readonly longForm!: Boolean;

  @Prop({default: false, })
  public readonly withCommunity!: Boolean;

  @Prop({default: true, })
  public readonly withCountry!: Boolean;

  @Prop({default: () => [], },)
  public readonly addresses!: Address[];

  @Prop({default: false, },)
  public readonly readonly!: boolean;

  @Prop({default: () => [], },)
  public readonly disabledFields!: AddressKeys[];

  @Prop({ default: () => {}, })
  requiredFields!: RequiredFields;

  hasRequiredField(fieldName: string): boolean {
    return this.requiredFields?.[fieldName] !== undefined;
  }

  isRequiredField(fieldName: string): boolean {
    return !!this.requiredFields?.[fieldName];
  }

  isRequiredEnabled(fieldName: string): boolean {
    return this.hasRequiredField(fieldName) ? this.isRequiredField(fieldName) : this.required;
  }

  public multiformDictionary = MultiformDictionary;
  public geoAreas = useGeoAreas();
  public getCommunitiesFunc = getCommunityByDistrictId;
  public initialStreetNumber = this.value?.streetNum;
  public I18NGetter: () => I18NInterface = I18NGetter;
  public get type(): AutocompletePlacesTypes {
    return this.dense
      ? AutocompletePlacesTypes.CITIES
      : AutocompletePlacesTypes.CITIES_AND_ROUTES;
  }

  public onAutocomplete(foundAddress: Address) {
    if (!this.value) {
      this.$emit('input', new Address());
    }
    this.value!.city = foundAddress.city;
    this.value!.country = foundAddress.country;
    this.value!.districtId = foundAddress.districtId;
    this.value!.landId = foundAddress.landId;
    this.value!.street = foundAddress.street;
    this.value!.post = foundAddress.post;
    this.value!.zip = foundAddress.zip;
    this.$emit('onAutocomplete', foundAddress);
  }

  // Jeżeli sie usuwa numer domu to ma być informacja o tym ,że nie można tego zrobić
  // Jeżeli po raz pierwszy wypelnia się wniosek to ma tylko zaznaczyć ,że pole jest wymagane
  public checkIfInitialStreetNumberExists(v: string) {
    return !(this.initialStreetNumber?.length && !v) || 'Numer domu nie może być pusty';
  }

  public setWhitespaceIfNull(v: string | null) {
    this.value!.streetLocal = this.value!.streetLocal ?? ' ';
  }
}
</script>

<style scoped lang="scss">
h2 {
  margin: 16px 24px 24px 0;
  font-size: 16px;
  line-height: 24px;
}
</style>
